import { randomId } from '../utils/randomId';
import { HostTakeoverGameEvent, NextGameParticipationGameEvent, OvertakeGameEvent, PenaltyGameEvent, ScoreGameEvent, DiceRollGameEvent, DiceKeepGameEvent, TurnEndGameEvent, PlayerReadyGameEvent, OrderFinalizedGameEvent, GameCreatedGameEvent, SettingsChangedGameEvent, GameRestartedGameEvent, ZoneTriggeredGameEvent } from '../types/game';
import type { ZoneChainResult } from './zones';

/**
 * Creates a SCORE event with computed resulting score.
 */
export function makeScoreEvent(turn: number, playerId: string, delta: number, resultingScore: number): ScoreGameEvent {
  const id = randomId();
  return { id, type: 'SCORE', turn, timestamp: new Date().toISOString(), payload: { playerId, delta, resultingScore, eventId: id } };
}

/**
 * Creates a PENALTY event with resulting score and reason.
 */
export function makePenaltyEvent(turn: number, playerId: string, delta: number, reason: string, resultingScore: number): PenaltyGameEvent {
  const id = randomId();
  return { id, type: 'PENALTY', turn, timestamp: new Date().toISOString(), payload: { playerId, delta, reason, resultingScore, eventId: id } };
}

/**
 * Creates an OVERTAKE event with bonus and penalties.
 */
export function makeOvertakeEvent(turn: number, overtakerId: string, bonus: number, penalties: { playerId: string; delta: number }[], stages?: { players: string[]; cumulativeBonus: number; resultingScore: number; }[]): OvertakeGameEvent {
  const id = randomId();
  return { id, type: 'OVERTAKE', turn, timestamp: new Date().toISOString(), payload: { overtakerId, bonus, penalties, stages, eventId: id } };
}

/**
 * Creates a ZONE_TRIGGERED event. Niesie CAŁY łańcuch stref razem z wyprzedzeniami,
 * które wywołał – dzięki temu odkrycie, punkty i cofnięcie są jedną, atomową zmianą,
 * a log i animacja mają z czego odtworzyć przebieg.
 *
 * Pola płaskie (`zoneId`, `zoneDelta`, `overtakeBonus`, …) opisują sumę ZALICZONYCH
 * ogniw i pozostają jedynym źródłem prawdy dla punktacji: reducer, undo i agregator
 * wyników liczą wyłącznie z nich, więc unieważniona część pętli nigdy nie dotknie
 * wyniku, choć jedzie w `links` do pokazania graczowi.
 */
export function makeZoneTriggeredEvent(
  turn: number,
  playerId: string,
  chain: ZoneChainResult,
): ZoneTriggeredGameEvent {
  const id = randomId();
  const headZone = chain.links[0].zone;
  return {
    id,
    type: 'ZONE_TRIGGERED',
    turn,
    timestamp: new Date().toISOString(),
    payload: {
      playerId,
      zoneId: headZone.id,
      kind: headZone.kind,
      tier: headZone.tier,
      start: headZone.start,
      end: headZone.end,
      zoneDelta: chain.zoneDelta,
      overtakeBonus: chain.overtakeBonus,
      penalties: chain.penalties,
      triggerScore: chain.links[0].triggerScore,
      resultingScore: chain.finalScore,
      links: chain.links.map((link) => ({
        zoneId: link.zone.id,
        kind: link.zone.kind,
        tier: link.zone.tier,
        start: link.zone.start,
        end: link.zone.end,
        zoneDelta: link.zoneDelta,
        overtakeBonus: link.overtakeBonus,
        penalties: link.penalties,
        triggerScore: link.triggerScore,
        resultingScore: link.resultingScore,
      })),
      settledIndex: chain.settledIndex,
      loop: chain.loop,
      eventId: id,
    },
  };
}

/**
 * Creates a DICE_ROLL event for a specific roll.
 */
export function makeDiceRollEvent(turn: number, playerId: string, rollIndex: number, dice: number[], keptMask: boolean[], isHotDiceReset: boolean = false): DiceRollGameEvent {
  const id = randomId();
  return { id, type: 'DICE_ROLL', turn, timestamp: new Date().toISOString(), payload: { playerId, rollIndex, dice, keptMask, isHotDiceReset, eventId: id } };
}

/**
 * Creates a DICE_KEEP event for the current kept mask.
 */
export function makeDiceKeepEvent(turn: number, playerId: string, rollIndex: number, keptMask: boolean[]): DiceKeepGameEvent {
  const id = randomId();
  return { id, type: 'DICE_KEEP', turn, timestamp: new Date().toISOString(), payload: { playerId, rollIndex, keptMask, eventId: id } };
}

/**
 * Creates a TURN_END event with final dice and points.
 */
export function makeTurnEndEvent(
  turn: number,
  playerId: string,
  finalDice: number[],
  basePoints: number,
  options?: { overshotWinScore?: boolean },
): TurnEndGameEvent {
  const id = randomId();
  return {
    id,
    type: 'TURN_END',
    turn,
    timestamp: new Date().toISOString(),
    payload: {
      playerId,
      finalDice,
      basePoints,
      ...(options?.overshotWinScore ? { overshotWinScore: true } : {}),
      eventId: id,
    },
  };
}

/**
 * Creates a PLAYER_READY event for lobby readiness.
 */
export function makePlayerReadyEvent(playerId: string, ready: boolean): PlayerReadyGameEvent {
  const id = randomId();
  return { id, type: 'PLAYER_READY', turn: 0, timestamp: new Date().toISOString(), payload: { playerId, ready, eventId: id } };
}

/**
 * Creates an ORDER_FINALIZED event for the lobby order.
 */
export function makeOrderFinalizedEvent(order: string[]): OrderFinalizedGameEvent {
  const id = randomId();
  return { id, type: 'ORDER_FINALIZED', turn: 0, timestamp: new Date().toISOString(), payload: { order, eventId: id } };
}

/**
 * Creates a GAME_CREATED event.
 */
export function makeGameCreatedEvent(mode: 'ONLINE' | 'REAL' = 'ONLINE'): GameCreatedGameEvent {
  const id = randomId();
  return { id, type: 'GAME_CREATED', turn: 0, timestamp: new Date().toISOString(), payload: { eventId: id, mode } };
}

/**
 * Creates a SETTINGS_CHANGED event from host input.
 */
export function makeSettingsChangedEvent(
  hostId: string,
  settings: SettingsChangedGameEvent['payload']['settings'],
): SettingsChangedGameEvent {
  const id = randomId();
  return { id, type: 'SETTINGS_CHANGED', turn: 0, timestamp: new Date().toISOString(), payload: { hostId, settings, eventId: id } };
}

/**
 * Creates a HOST_TAKEOVER event.
 */
export function makeHostTakeoverEvent(newHostPlayerId: string, reason: string = 'TIMEOUT'): HostTakeoverGameEvent {
  const id = randomId();
  return {
    id,
    type: 'HOST_TAKEOVER',
    turn: 0,
    timestamp: new Date().toISOString(),
    payload: { newHostPlayerId, reason, eventId: id },
  };
}

/**
 * Creates a NEXT_GAME_PARTICIPATION event for the bool-only post-game contract.
 * `participating=true` stores the player in the map, `false` removes the entry.
 */
export function makeNextGameParticipationEvent(playerId: string, participating: boolean): NextGameParticipationGameEvent {
  const id = randomId();
  return {
    id,
    type: 'NEXT_GAME_PARTICIPATION',
    turn: 0,
    timestamp: new Date().toISOString(),
    payload: { playerId, participating, eventId: id },
  };
}

/**
 * Creates a GAME_RESTARTED event for an in-place room reset into the next round.
 */
export function makeGameRestartedEvent(
  roomId: string,
  options?: { currentRoundId?: string | null },
): GameRestartedGameEvent {
  const id = randomId();
  return {
    id,
    type: 'GAME_RESTARTED',
    turn: 0,
    timestamp: new Date().toISOString(),
    payload: {
      newGameId: roomId,
      eventId: id,
      ...(typeof options?.currentRoundId === 'string' && options.currentRoundId.trim().length > 0
        ? { currentRoundId: options.currentRoundId.trim().toUpperCase() }
        : {}),
    },
  };
}
